import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatError } from "../lib/api";

const STAGES = [
  { id: "intake_received", label: "Intake Received", note: "The Lion Chief has your intake and is preparing to begin." },
  { id: "in_preparation", label: "In Preparation", note: "Your candle is being hand-poured with selected herbs, oils, and prayer." },
  { id: "blessed", label: "Blessed", note: "Your candle has been blessed and sealed." },
  { id: "ready_to_ship", label: "Ready to Ship", note: "Your candle is packaged and queued for shipping." },
  { id: "shipped", label: "Shipped", note: "Your candle is on its way to you." },
  { id: "completed", label: "Completed", note: "Your candle journey is complete." },
];

export default function CustomCandleTracker() {
  const { intakeId } = useParams();
  const [intake, setIntake] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/custom-intakes/${intakeId}/public`)
      .then((r) => setIntake(r.data))
      .catch((e) => setError(formatError(e)));
  }, [intakeId]);

  if (error) {
    return (
      <div className="py-32 px-6 text-center max-w-xl mx-auto" data-testid="tracker-error">
        <div className="overline mb-4">Tracker</div>
        <h1 className="font-serif text-3xl mb-6">We couldn't find that order.</h1>
        <p className="text-white/55">Please check the link in your confirmation email, or reach out via the contact page.</p>
        <Link to="/contact" className="inline-block mt-8 text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white">Contact Us →</Link>
      </div>
    );
  }
  if (!intake) {
    return <div className="py-32 text-center text-white/40" data-testid="tracker-loading">Loading your candle…</div>;
  }

  const currentIndex = intake.paid ? Math.max(0, STAGES.findIndex((s) => s.id === intake.candle_status)) : -1;
  const currentStage = currentIndex >= 0 ? STAGES[currentIndex] : null;

  return (
    <div className="py-16 md:py-24 px-6 md:px-10 max-w-3xl mx-auto" data-testid="tracker-page">
      <div className="overline mb-4">Your Custom Hand-Poured Candle</div>
      <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
        {intake.full_name}'s candle journey.
      </h1>
      <p className="mt-4 text-[#D4AF37] font-serif italic" data-testid="tracker-intention">
        Intention: {intake.primary_intention}
      </p>

      {!intake.paid && (
        <div className="mt-10 border border-yellow-500/30 bg-yellow-500/5 px-6 py-5" data-testid="tracker-unpaid">
          <div className="overline text-yellow-400 mb-2">Awaiting Payment</div>
          <p className="text-sm text-white/70 leading-relaxed">
            Your intake has been received but payment hasn't been completed yet. Once payment clears,
            The Lion Chief will begin your candle.
          </p>
        </div>
      )}

      {intake.paid && currentStage && (
        <div className="mt-12 border border-[#D4AF37]/30 bg-[#0A0A0A] px-6 py-6" data-testid="tracker-current-stage">
          <div className="overline text-[#D4AF37] mb-2">Current Status</div>
          <div className="font-serif text-2xl">{currentStage.label}</div>
          <p className="mt-3 text-white/70 leading-relaxed text-sm">{currentStage.note}</p>
        </div>
      )}

      <div className="mt-10" data-testid="tracker-stages">
        <div className="overline text-[#D4AF37] mb-6">Journey</div>
        <ol className="space-y-5">
          {STAGES.map((s, i) => {
            const reached = intake.paid && i <= currentIndex;
            const isCurrent = i === currentIndex;
            return (
              <li key={s.id} className="flex gap-4" data-testid={`tracker-stage-${s.id}`}>
                <div className="flex flex-col items-center pt-1">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      reached ? "bg-[#D4AF37]" : "border border-white/25"
                    } ${isCurrent ? "ring-4 ring-[#D4AF37]/20" : ""}`}
                  />
                  {i < STAGES.length - 1 && (
                    <div className={`w-px flex-1 mt-2 ${reached && i < currentIndex ? "bg-[#D4AF37]/50" : "bg-white/10"}`} style={{ minHeight: 32 }} />
                  )}
                </div>
                <div className="pb-5">
                  <div className={`font-serif text-lg ${reached ? "text-white" : "text-white/40"}`}>{s.label}</div>
                  {isCurrent && <p className="text-sm text-white/55 mt-1 leading-relaxed">{s.note}</p>}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="mt-12 pt-8 border-t border-white/10 text-sm text-white/50 leading-relaxed">
        <p>
          Every Custom Hand-Poured Candle is individually crafted by The Lion Chief using carefully selected
          herbs, oils, prayer, and intention based on the information you provided. No two candles are
          exactly alike. Typical preparation is 7–10 business days.
        </p>
        <p className="mt-4">
          Questions? Reply to your confirmation email or visit our{" "}
          <Link to="/contact" className="text-[#D4AF37] hover:text-white">contact page</Link>.
        </p>
      </div>
    </div>
  );
}
